import {useEffect, useState} from 'react';
import {useStoreState, useStoreActions} from 'store';
import {buildURL} from '@/utils/urlBuilder';

interface ConfigData {
    FCONST: any;
    SCONSTANTS: any;
    DCONSTANTS: any;
    URLS: any;
}

interface UseOptimizedConfigResult {
    config: ConfigData | null;
    userData: any | null;
    loading: boolean;
    error: Error | null;
}

const useOptimizedConfig = (): UseOptimizedConfigResult => {
    
    const [config, setConfig] = useState<ConfigData | null>(null);
    const [userData, setUserData] = useState<any | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    
    
    // Listen to userData store reload flag to trigger refresh when needed
    const userDataReload = useStoreState((state) => state.userData.reload);
    const setUserDataReload = useStoreActions((actions) => actions.userData.setReload);

    useEffect(() => {
        
        const handleConfigLoaded = (event: CustomEvent) => {
            if (event.detail.error) {
                console.error('❌ useOptimizedConfig: Config loading error', event.detail.error);
                setError(event.detail.error);
            } else {
                const configData = {
                    FCONST: window.FCONST || {},
                    SCONSTANTS: window.SCONSTANTS || {},
                    DCONSTANTS: window.DCONSTANTS || {},
                    URLS: window.URLS || {}
                };
                setConfig(configData);
                setUserData(event.detail.userData || window.USER_DATA || null);
            }
            setLoading(false);
        };

        // Check if config is already loaded
        
        if (window.FCONST && Object.keys(window.FCONST).length > 1) {
            const configData = {
                FCONST: window.FCONST,
                SCONSTANTS: window.SCONSTANTS || {},
                DCONSTANTS: window.DCONSTANTS || {},
                URLS: window.URLS || {}
            };
            setConfig(configData);

            
            // If no user data is available, try to fetch it
            if (!(window as any).USER_DATA) {
                fetch(buildURL('currentUserResource'), { 
                    credentials: 'same-origin',
                    headers: {'Accept': 'application/json'}
                })
                    .then(response => {
                        if (!response.ok) {throw new Error('User data fetch failed: ' + response.status);}
                        return response.json();
                    })
                    .then(userData => {
                        setUserData(userData);
                        (window as any).USER_DATA = userData;
                        setLoading(false);
                    })
                    .catch(err => {
                        console.error('❌ useOptimizedConfig: User data fetch failed:', err);
                        setError(err);
                        setLoading(false);
                    });
            } else {
                setUserData((window as any).USER_DATA);
                setLoading(false);
            }
        } else {
            // Listen for config loaded event
            window.addEventListener('configLoaded', handleConfigLoaded as EventListener);
            
            // Trigger config loading if not already started
            if (typeof window.loadConfig === 'function') {
                window.loadConfig().catch((err: Error) => {
                    setError(err);
                    setLoading(false);
                });
            }
        }

        return () => {
            window.removeEventListener('configLoaded', handleConfigLoaded as EventListener);
        };
    }, []);

    // Handle userData reload requests
    useEffect(() => {
        if (userDataReload && typeof window.loadConfig === 'function') {
            setLoading(true);
            // Force reload user data by calling the API directly
            fetch(buildURL('currentUserResource'), { 
                credentials: 'same-origin',
                headers: {'Accept': 'application/json'},
                cache: 'no-cache' // Force fresh data
            })
                .then(response => {
                    if (!response.ok) {throw new Error('User data reload failed: ' + response.status);}
                    return response.json();
                })
                .then(freshUserData => {
                    setUserData(freshUserData);
                    window.USER_DATA = freshUserData; // Update global cache
                    setUserDataReload(false); // Reset reload flag
                    setLoading(false);
                })
                .catch(err => {
                    console.error('❌ useOptimizedConfig: User data reload failed:', err);
                    setError(err);
                    setLoading(false);
                });
        }
    }, [userDataReload, setUserDataReload]);

    
    return {config, userData, loading, error};
};

export default useOptimizedConfig;
